// The activity change log page (/instructor/change_logs/): a per-setting history and the list of every save,
// both client-backed MeTables fed by one request to instructor/views/change_logs.py::table_data.

import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'

import { MeTable } from '@CommonComponents'
import { client } from '@Utils'

const Muted = ({ children }) => <span className="text-muted">{children}</span>

Muted.propTypes = { children: PropTypes.node.isRequired }

const whenColumn = {
  key: 'when',
  label: 'When',
  type: 'text',
  sortable: true,
  required: true,
  wrap: 'nowrap',
  width: 200,
  value: row => row.when.iso,
  render: row => row.when.label,
}

const userColumn = {
  key: 'user',
  label: 'Changed by',
  type: 'text',
  width: 170,
  sortable: true,
  searchable: true,
  value: row => row.user.name || row.user.email,
  searchText: row => `${row.user.name} ${row.user.email}`,
  render: row => (
    <>
      {row.user.name || row.user.email}
      {row.user.name && (
        <span className="d-block small text-muted">{row.user.email}</span>
      )}
    </>
  ),
}

const viewColumn = { key: 'view', label: 'Log', type: 'link', width: 90 }

// a link cell reads {label, href} from its own field
const withViewLink = label => row => ({
  ...row,
  view: {
    label,
    href: row.url,
    ariaLabel: `${label} from ${row.when.label}`,
  },
})

// a value the log stored; null was stored as empty, and a first record has no earlier value at all
const valueCell = (row, field) => {
  if (field === 'before' && row.kind === 'First recorded')
    return <Muted>First recorded</Muted>
  if (row[field] === null || row[field] === '') return <Muted>Empty</Muted>
  return row[field]
}

const valueColumn = (key, label) => ({
  key,
  label,
  type: 'text',
  searchable: true,
  wrap: 'wrap',
  width: 160,
  value: row => row[key] || '',
  searchText: row => row[key] || '',
  render: row => valueCell(row, key),
})

export const historyConfig = {
  tableId: 'change-logs-history',
  caption: 'Setting history',
  description:
    'One row each time a setting’s value changed. Only saves that write a change log appear here (the settings form, availability, LMS visibility, due dates and Claude edits); bulk updates, course copies and LMS syncs do not.',
  rowKey: 'id',
  columns: [
    whenColumn,
    {
      key: 'setting',
      label: 'Setting',
      type: 'badge',
      sortable: true,
      searchable: true,
      filterable: true,
      wrap: 'nowrap',
      width: 190,
      className: 'me-table-id',
      value: row => row.setting,
      searchText: row => row.setting,
      render: row => row.setting,
    },
    valueColumn('before', 'From'),
    valueColumn('after', 'To'),
    userColumn,
    viewColumn,
  ],
  defaultSort: { key: 'when', direction: 'descending' },
  pageSizes: [25, 50, 100],
  defaultPageSize: 25,
  source: { mode: 'client', max: 20000 },
  searchLabel: 'Search setting history',
  searchPlaceholder: 'Search setting, value or person',
  emptyState: {
    title: 'No setting changes',
    description: 'Nothing has been logged for this activity yet.',
  },
}

const changedSummary = row => {
  if (row.changed.length) return row.changed.join(', ')
  if (row.first_recorded)
    return `First recorded (${row.first_recorded} settings)`
  return ''
}

export const allConfig = {
  tableId: 'change-logs-all',
  caption: 'All changes',
  rowKey: 'id',
  columns: [
    whenColumn,
    userColumn,
    {
      key: 'changed',
      label: 'Settings changed',
      type: 'text',
      searchable: true,
      wrap: 'wrap',
      value: changedSummary,
      searchText: changedSummary,
      render: row => changedSummary(row) || <Muted>No settings changed</Muted>,
    },
    viewColumn,
  ],
  defaultSort: { key: 'when', direction: 'descending' },
  pageSizes: [25, 50, 100],
  defaultPageSize: 25,
  source: { mode: 'client', max: 5000 },
  searchLabel: 'Search changes',
  searchPlaceholder: 'Search setting or person',
  emptyState: {
    title: 'No changes',
    description: 'There are no logs for this activity.',
  },
}

// One request feeds both tables; Retry on either reloads it for both.
export const createLoader = (dataUrl, request = url => client.get(url)) => {
  let pending = null
  const listeners = new Set()
  const load = () => {
    pending = request(dataUrl).then(response => response.data)
    return pending
  }
  return {
    get: () => pending || load(),
    // a retry: every table follows the new request
    reload: () => {
      load()
      listeners.forEach(listener => listener(pending))
      return pending
    },
    subscribe: listener => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
  }
}

const useLoaded = (loader, field, decorate) => {
  const [state, setState] = useState({ status: 'loading', rows: [] })
  const follow = useCallback(
    promise => {
      setState(current => ({ ...current, status: 'loading' }))
      promise
        .then(data =>
          setState({ status: 'ready', rows: data[field].map(decorate) })
        )
        .catch(() => setState(current => ({ ...current, status: 'error' })))
    },
    [field, decorate]
  )
  useEffect(() => {
    follow(loader.get())
    return loader.subscribe(follow)
  }, [loader, follow])
  return state
}

const TABLES = {
  history: {
    field: 'changes',
    config: historyConfig,
    decorate: withViewLink('View'),
  },
  all: { field: 'logs', config: allConfig, decorate: withViewLink('View') },
}

export const ChangeLogTable = ({ loader, kind }) => {
  const { field, config, decorate } = TABLES[kind]
  const { status, rows } = useLoaded(loader, field, decorate)
  return (
    <MeTable
      config={config}
      rows={rows}
      status={status}
      onRetry={loader.reload}
    />
  )
}

ChangeLogTable.propTypes = {
  loader: PropTypes.object.isRequired,
  kind: PropTypes.oneOf(['history', 'all']).isRequired,
}
